import { useEffect, useMemo, useState } from "react";
import { TextField, Dialog, DialogActions, DialogContent, DialogTitle, Button, Alert, Divider, Chip, Grid, IconButton, Tooltip, InputAdornment } from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import { Icons } from "../../../utils/Icon";

const generateLicenseCode = () => {
    if (!window.crypto || !window.crypto.getRandomValues) {
        throw new Error("Secure random generator is unavailable.");
    }

    const alphabet = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ";
    const randomValues = new Uint32Array(16);
    window.crypto.getRandomValues(randomValues);

    const rawCode = Array.from(randomValues, (value) => alphabet[value % alphabet.length]).join("");
    return rawCode.match(/.{4}/g).join("-");
};

const buildDefaultData = () => ({
    remarks: generateLicenseCode(),
    product_code: "",
    customer_code: "",
    customer_name: "",
    customer_email: "",
    status: "active",
    expires_at: moment().add(1, "year"),
    offline_days: 30,
});

export default function CreateUtilityLicenseV2Form({ open, handleClose, fetchData }) {
    const [newData, setNewData] = useState(null);
    const [errors, setErrors] = useState({});
    const [helperText, setHelperText] = useState({});
    const [errorMessage, setErrorMessage] = useState("");

    const requiredFields = useMemo(() => ["remarks", "product_code", "customer_name", "expires_at"], []);

    useEffect(() => {
        if (open) {
            setErrors({});
            setHelperText({});
            setErrorMessage("");

            try {
                setNewData(buildDefaultData());
            } catch (error) {
                setNewData({
                    remarks: "",
                    product_code: "",
                    customer_code: "",
                    customer_name: "",
                    customer_email: "",
                    status: "active",
                    expires_at: moment().add(1, "year"),
                    offline_days: 30,
                });
                setErrorMessage(error.message);
            }
        }
    }, [open]);

    if (!newData) {
        return null;
    }

    const setError = (field, message) => {
        setErrors((prevErrors) => ({ ...prevErrors, [field]: true }));
        setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: message }));
    };

    const clearError = (field) => {
        setErrors((prevErrors) => ({ ...prevErrors, [field]: false }));
        setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: "" }));
    };

    const validate = (field, value) => {
        const isRequired = requiredFields.includes(field);

        if (isRequired && (!value || value === "")) {
            setError(field, "This field is required.");
            return false;
        }

        if (field === "customer_email" && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            setError(field, "Please enter a valid email address.");
            return false;
        }

        clearError(field);
        return true;
    };

    const handleRegenerateLicenseCode = () => {
        try {
            const regeneratedCode = generateLicenseCode();
            setNewData((prevState) => ({ ...prevState, remarks: regeneratedCode }));
            clearError("remarks");
            setErrorMessage("");
        } catch (error) {
            setErrorMessage(error.message);
        }
    };

    const handleCreate = async () => {
        let isValid = true;

        requiredFields.forEach((field) => {
            const value = field === "expires_at" ? (newData.expires_at ? moment(newData.expires_at).format("YYYY-MM-DD") : "") : newData[field];
            if (!validate(field, value)) {
                isValid = false;
            }
        });

        if (!validate("customer_email", newData.customer_email || "")) {
            isValid = false;
        }

        if (!isValid) {
            setErrorMessage("Please fill all required fields correctly.");
            return;
        }

        setErrorMessage("");

        try {
            const payload = {
                license_code: newData.remarks,
                product_code: newData.product_code,
                customer_code: newData.customer_code || undefined,
                customer_name: newData.customer_name,
                customer_email: newData.customer_email || undefined,
                expires_at: moment(newData.expires_at).format("YYYY-MM-DD"),
                offline_days: newData.offline_days,
            };

            const response = await axios.post(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v2/utility-license/create", payload);

            if (response.status === 201) {
                handleClose();
                Swal.fire({
                    title: "Success",
                    text: "Utility license v2 created successfully",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 2000,
                });
                fetchData();
            }
        } catch (error) {
            if (error.response && error.response.data) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage("An error occurred while creating the utility license v2. Please try again later.");
            }
        }
    };

    return (
        <Dialog
            maxWidth="md"
            fullWidth
            open={open}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">Create New Utility License v2</DialogTitle>
            {errorMessage && (
                <Alert variant="outlined" severity="error" style={{ marginLeft: "24px", marginRight: "24px" }}>
                    {errorMessage}
                </Alert>
            )}
            <DialogContent style={{ paddingBottom: "0px" }}>
                <Divider style={{ paddingTop: "0px", paddingBottom: "4px" }}>
                    <Chip label="Utility License v2 Details" />
                </Divider>
                <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoFocus
                            autoComplete="off"
                            margin="dense"
                            id="remarks"
                            label="License Code"
                            type="text"
                            fullWidth
                            required
                            value={newData.remarks}
                            error={Boolean(errors.remarks)}
                            helperText={helperText.remarks}
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title="Regenerate License Code">
                                            <IconButton color="primary" edge="end" aria-label="regenerate license code" onClick={handleRegenerateLicenseCode}>
                                                <Icons.Refresh />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ backgroundColor: "#f1f0f0" }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="product_code"
                            label="Product Code"
                            type="text"
                            fullWidth
                            required
                            value={newData.product_code}
                            error={Boolean(errors.product_code)}
                            helperText={helperText.product_code}
                            onChange={(event) => {
                                const value = event.target.value || "";
                                setNewData({ ...newData, product_code: value });
                                validate("product_code", value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="customer_code"
                            label="Customer Code"
                            type="text"
                            fullWidth
                            value={newData.customer_code}
                            onChange={(event) => {
                                setNewData({ ...newData, customer_code: event.target.value || "" });
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="customer_name"
                            label="Customer Name"
                            type="text"
                            fullWidth
                            required
                            value={newData.customer_name}
                            error={Boolean(errors.customer_name)}
                            helperText={helperText.customer_name}
                            onChange={(event) => {
                                const value = event.target.value || "";
                                setNewData({ ...newData, customer_name: value });
                                validate("customer_name", value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="customer_email"
                            label="Customer Email"
                            type="email"
                            fullWidth
                            value={newData.customer_email}
                            error={Boolean(errors.customer_email)}
                            helperText={helperText.customer_email}
                            onChange={(event) => {
                                const value = event.target.value || "";
                                setNewData({ ...newData, customer_email: value });
                                validate("customer_email", value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="status"
                            label="Status"
                            type="text"
                            fullWidth
                            value={newData.status}
                            InputProps={{ readOnly: true }}
                            sx={{ backgroundColor: "#f1f0f0" }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                label="Expires At"
                                inputFormat="YYYY-MM-DD"
                                value={newData.expires_at}
                                onChange={(newValue) => {
                                    setNewData({ ...newData, expires_at: newValue });
                                    validate("expires_at", newValue ? moment(newValue).format("YYYY-MM-DD") : "");
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        required
                                        margin="dense"
                                        fullWidth
                                        error={Boolean(errors.expires_at)}
                                        helperText={helperText.expires_at}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete="off"
                            margin="dense"
                            id="offline_days"
                            label="Offline Days"
                            type="number"
                            fullWidth
                            value={newData.offline_days}
                            InputProps={{ readOnly: true }}
                            sx={{ backgroundColor: "#f1f0f0" }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions style={{ margin: "12px" }}>
                <Button onClick={handleCreate} variant="contained" color="primary" style={{ marginRight: "6px", color: "white" }}>
                    Create
                </Button>
                <Button onClick={handleClose} variant="contained" color="error" style={{ marginRight: "6px", color: "white" }}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
